import React, { memo, useCallback } from 'react';

interface ControllerStatusBarProps {
  deratePower: number;
  increasePower: number;
  deratingEmergency: boolean;
  exportLimitEnabled: boolean;
  curtailedPower?: number;
  currentRateLimit?: number;
  batteryHeadroom?: number;
  batteryAbsorptionExpected?: number;
}

export const ControllerStatusBar = memo(function ControllerStatusBar({
  deratePower,
  increasePower,
  deratingEmergency,
  exportLimitEnabled,
  curtailedPower = 0,
  currentRateLimit = 10000,
  batteryHeadroom = 0,
  batteryAbsorptionExpected = 0,
}: ControllerStatusBarProps) {
  const formatPower = (watts: number) => {
    if (Math.abs(watts) >= 1000) return `${(watts / 1000).toFixed(1)} kW`;
    return `${Math.round(watts)} W`;
  };

  const rateLimitPercent = (currentRateLimit / 100).toFixed(0);
  const isLimiting = currentRateLimit < 10000;

  return (
    <div className="dashboard-card p-3 sm:p-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4">
        {/* Curtailed Power - New prominent display */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className={`w-1.5 sm:w-2 h-6 sm:h-8 rounded-full ${curtailedPower > 100 ? 'bg-orange-500' : 'bg-slate-600'}`}></div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase">Curtailed</p>
            <p className={`text-xs sm:text-sm font-medium ${curtailedPower > 100 ? 'text-orange-400' : 'text-slate-400'}`}>
              {formatPower(curtailedPower)}
            </p>
          </div>
        </div>

        {/* Power Limit Percentage */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="relative w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0">
            <svg viewBox="0 0 40 40" className="w-full h-full transform -rotate-90">
              <circle
                className="text-slate-700"
                strokeWidth="3"
                stroke="currentColor"
                fill="transparent"
                r="16"
                cx="20"
                cy="20"
              />
              <circle
                className={isLimiting ? 'text-orange-500' : 'text-green-500'}
                strokeWidth="3"
                strokeDasharray={`${(currentRateLimit / 10000) * 100.53} 100.53`}
                strokeLinecap="round"
                stroke="currentColor"
                fill="transparent"
                r="16"
                cx="20"
                cy="20"
              />
            </svg>
            <span className={`absolute inset-0 flex items-center justify-center text-[10px] sm:text-xs font-bold ${isLimiting ? 'text-orange-400' : 'text-green-400'}`}>
              {rateLimitPercent}%
            </span>
          </div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase">Limit</p>
            <p className={`text-xs sm:text-sm font-medium ${isLimiting ? 'text-orange-400' : 'text-green-400'}`}>
              {isLimiting ? 'Limiting' : 'Full'}
            </p>
          </div>
        </div>

        {/* Battery Headroom */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className={`w-1.5 sm:w-2 h-6 sm:h-8 rounded-full ${batteryHeadroom > 0 ? 'bg-purple-500' : 'bg-slate-600'}`}></div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase">Battery</p>
            <p className={`text-xs sm:text-sm font-medium ${batteryHeadroom > 0 ? 'text-purple-400' : 'text-slate-400'}`}>
              {formatPower(batteryHeadroom)}
            </p>
          </div>
        </div>

        {/* Derate Power */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className={`w-1.5 sm:w-2 h-6 sm:h-8 rounded-full ${deratePower > 0 ? 'bg-yellow-500' : 'bg-slate-600'}`}></div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase">Derate</p>
            <p className={`text-xs sm:text-sm font-medium ${deratePower > 0 ? 'text-yellow-400' : 'text-slate-400'}`}>
              {formatPower(deratePower)}
            </p>
          </div>
        </div>

        {/* Increase Power */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className={`w-1.5 sm:w-2 h-6 sm:h-8 rounded-full ${increasePower > 0 ? 'bg-green-500' : 'bg-slate-600'}`}></div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase">Increase</p>
            <p className={`text-xs sm:text-sm font-medium ${increasePower > 0 ? 'text-green-400' : 'text-slate-400'}`}>
              {formatPower(increasePower)}
            </p>
          </div>
        </div>

        {/* Emergency Status */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${deratingEmergency ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`}></div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase">Emergency</p>
            <p className={`text-xs sm:text-sm font-medium ${deratingEmergency ? 'text-red-400' : 'text-green-400'}`}>
              {deratingEmergency ? 'Active' : 'None'}
            </p>
          </div>
        </div>

        {/* Export Limit */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${exportLimitEnabled ? 'bg-blue-500' : 'bg-slate-600'}`}></div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase">Export</p>
            <p className={`text-xs sm:text-sm font-medium ${exportLimitEnabled ? 'text-blue-400' : 'text-slate-400'}`}>
              {exportLimitEnabled ? 'On' : 'Off'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ControllerStatusBar;
